/**
 * jobVisit (INSTALL-PATH-01 + JOB-DOCS-02 + PERMIT-01 on the phone, Seth
 * 2026-09-24) — what a crew sees ON SITE at an install or new-construction
 * visit, in place of the service-call invoice.
 *
 *   · INSTALL-PATH-01: an install visit finished on the iPad used to run the
 *     service-call path and would have billed a SERVICE invoice. Install and
 *     new-construction work bills through the JOB (close-out / builder draws),
 *     so a job visit never touches the service invoice — here, or in the
 *     database (save_invoice_draft refuses it).
 *   · Scope: what's going in and the install details the salesperson caught —
 *     no cost, no margin (Seth's rule: installers see customer prices at the
 *     walkthrough, NC crews see none).
 *   · Documents: every job file the office marked "crew sees it" — plans,
 *     load calcs, scope, photos, spec sheets, permit paperwork. MARKUP-03: any
 *     photo or plan page can be marked up (a copy — the original stays), and
 *     Sketch draws on grid paper; both land back on the job.
 *   · Permit: who pulls it, its status and inspections, and "Request a
 *     permit" — the permit desk sees it at the top of its queue.
 *   · Wrap Up: a note for the customer (prints on the invoice), a note for the
 *     office (never prints), and done-for-today vs install complete.
 *
 * Competitor frame: ServiceTitan's field app shows the sold estimate and job
 * attachments; Buildertrend/Jobber give crews per-job files with permissions;
 * Jobber's Field Crew role sees no prices.
 */
import { useMemo, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft, Check, ChevronRight, ClipboardCheck, FileText, FolderOpen, HardHat, Loader2, Lock, Package,
  PenLine, Send, ShieldCheck, Wrench, X,
} from "lucide-react";
import { cn } from "@/lib/cn";
import { supabase } from "@/lib/supabase";
import { useRealtimeRefresh } from "@/lib/useRealtimeRefresh";
import { haptic } from "@/native/boot";
import {
  useJob, useJobEquipment, useJobPermits, usePermitInspections, useRequestPermit, useUpdateJob,
  effectivePermitBy, permitIsOnFile, isNCJob,
  PERMIT_STATUS_LABEL, INSPECTION_KIND_LABEL, INSPECTION_RESULT_LABEL,
  type Job, type JobPermit, type PermitInspection,
} from "@/features/jobs/jobs";
import { specGroups, specLines, useInstallBank, useJobEquipFacts } from "@/features/jobs/installSpec";
import { UnitLines } from "./UnitLines";
import {
  useJobFiles, useSignedUrls, useJobDocTypes, crewCanSee, jobDocTypeOf, prettySize, invalidateFileQueries, isImageFile, isPdfFile,
  JOB_DOC_TYPES_DEFAULT, type PegasusFile, type JobDocType,
} from "@/features/files/filesCore";
import { MarkupEditor } from "@/features/files/MarkupEditor";
import { usePostInstall } from "@/features/dispatch-detail/postInstall";
import { nextPickupDay } from "@/features/parts/pickupList";
import { EquipmentStep, ChangesStep, BeforeYouStartCard, Sheet, ReasonChips } from "./installWork";
import { BalanceCard, ReadyToFinishCard, AfterPhotosCard, SignOffCard, useInstallReadiness } from "./installWrap";
import { InstallChecklistCard, RebateTestCard } from "./installChecklist";
import { JobMaterialsCard } from "./materialRequest";
import { useCrewFinish } from "@/features/jobs/fieldPay";
import { NotesEditor } from "@/components/NotesEditor";

/** A dispatch that is a day of JOB work (an install / new-construction
 *  visit), not a service call. The originating service call linked to a job
 *  as "visit 1" and service-lane return visits keep the service path. */
export function isJobWorkVisit(t: { job_id?: string | null; lane_key?: string | null; visit_role?: string | null }): boolean {
  if (!t.job_id) return false;
  const lane = (t.lane_key ?? "").toLowerCase();
  const role = (t.visit_role ?? "").toLowerCase();
  return lane === "install" || lane === "nc" || role === "install" || /rough|trim|final|start ?up/.test(role);
}

const card = "rounded-3xl border border-black/[0.08] bg-white p-4 shadow-[0_1px_2px_rgba(16,24,40,0.04),0_5px_14px_-7px_rgba(16,24,40,0.14)]";

type StepKey = "scope" | "docs" | "equipment" | "changes" | "permit" | "wrap";
const STEP_LABEL: Record<StepKey, string> = {
  scope: "Scope", docs: "Documents", equipment: "Equipment", changes: "Changes", permit: "Permit", wrap: "Wrap Up",
};
/** Installs (FIELD-CREWS-01 phase 1): documents ride on Scope; Equipment
 *  (confirm each unit, the old unit out) and Changes (materials, add-ons the
 *  customer signs) are the installer's own steps. New construction keeps its
 *  own Documents step until the NC crew app (phase 2). */
// Seth 2026-09-24: on a retrofit the office pulls the permit — the crew can
// see it (bottom of Scope), it doesn't need a step.
const INSTALL_STEPS: StepKey[] = ["scope", "equipment", "changes", "wrap"];
const NC_STEPS: StepKey[] = ["scope", "docs", "permit", "wrap"];

export function JobVisitWork({ jobId, dispatchId, techFirst, empno, onWrap }: {
  jobId: string;
  /** The visit (pegasus_dispatch id) — completed when the install is done. */
  dispatchId: string | null;
  techFirst: string | null;
  /** The crew member (credited as "sold by" on add-ons). */
  empno: string | null;
  /** Stamps the field state wrapped (the board chip + the next stop). */
  onWrap: () => void;
}) {
  // The office's edits (permit filled in, a document added, equipment or
  // materials changed) land on the iPad within seconds — and the crew's land
  // in the office the same way.
  useRealtimeRefresh(
    ["pegasus_permit", "pegasus_permit_inspection", "pegasus_job", "pegasus_job_equipment", "pegasus_job_material", "pegasus_change_order", "pegasus_job_removed_equipment",
      "pegasus_refrigerant_log", "pegasus_job_check", "pegasus_rebate_test", "pegasus_material_request"],
    [["job_permits"], ["permit_inspections"], ["job"], ["job_equipment"], ["job_materials"], ["change_orders"], ["job_money"], ["job_removed_equipment"],
      ["job_refrigerant"], ["job_checks"], ["rebate_test"], ["rebate_test_status"], ["material_requests"]],
  );
  const { data: job, isLoading } = useJob(jobId);
  const [step, setStep] = useState<StepKey>("scope");
  const [seen, setSeen] = useState<Set<StepKey>>(() => new Set<StepKey>(["scope"]));
  const { data: files } = useJobFiles(jobId);
  const { data: typesQ } = useJobDocTypes();
  const types = typesQ ?? JOB_DOC_TYPES_DEFAULT;
  const crewFiles = useMemo(() => (files ?? []).filter((f) => crewCanSee(f, types)), [files, types]);
  const { data: permits } = useJobPermits(jobId);
  const { data: equip } = useJobEquipment(jobId);

  if (isLoading) return <div className="h-40 animate-pulse rounded-3xl bg-slate-200/60" />;
  if (!job) {
    return (
      <div className={card}>
        <p className="text-sm font-semibold text-slate-700">This visit's job isn't available.</p>
        <p className="mt-1 text-xs text-slate-500">Ask the office — the job may have been removed.</p>
      </div>
    );
  }
  const nc = isNCJob(job);
  const steps = nc ? NC_STEPS : INSTALL_STEPS;
  const cur = steps.includes(step) ? step : "scope";
  const units = (equip ?? []).filter((e) => e.kind !== "accessory" && e.kind !== "material");
  const permitOk = (permits ?? []).some(permitIsOnFile);
  const done: Record<StepKey, boolean> = {
    scope: false,
    docs: crewFiles.length === 0 || seen.has("docs"),
    equipment: units.length > 0 && units.every((u) => !!u.install_confirmed_at),
    changes: seen.has("changes"),
    permit: permitOk,
    wrap: false,
  };
  const go = (k: StepKey) => {
    haptic("light");
    setStep(k);
    setSeen((prev) => (prev.has(k) ? prev : new Set(prev).add(k)));
  };

  return (
    <>
      <div className="sticky top-0 z-10 -mx-3 border-b border-slate-200/80 bg-[#f6f5f1]/95 px-3 pb-1.5 pt-1 backdrop-blur">
        <div className="flex gap-1">
          {steps.map((k, i) => {
            const on = cur === k;
            return (
              <button key={k} type="button" onClick={() => go(k)}
                className={cn("flex min-w-0 flex-1 flex-col items-center gap-0.5 rounded-xl py-1.5", on ? "bg-slate-900 text-white" : "text-slate-500 active:bg-slate-200")}>
                <span className={cn("flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-extrabold",
                  on ? "bg-white/20 text-white" : done[k] ? "bg-emerald-500 text-white" : "bg-slate-200 text-slate-500")}>
                  {done[k] && !on ? <Check className="h-3 w-3" /> : i + 1}
                </span>
                <span className="max-w-full truncate text-[9.5px] font-bold leading-none">{STEP_LABEL[k]}{k === "docs" && crewFiles.length ? ` ${crewFiles.length}` : ""}</span>
              </button>
            );
          })}
        </div>
      </div>

      {cur === "scope" && <ScopeStep job={job} files={nc ? null : crewFiles} types={types} techFirst={techFirst} empno={empno} dispatchId={dispatchId} onSite />}
      {cur === "docs" && <DocsStep files={crewFiles} types={types} job={job} />}
      {cur === "equipment" && <EquipmentStep job={job} dispatchId={dispatchId} techFirst={techFirst} empno={empno} />}
      {cur === "changes" && <ChangesStep job={job} dispatchId={dispatchId} techFirst={techFirst} empno={empno} moneyCard={<BalanceCard job={job} collectable={false} />} />}
      {cur === "permit" && <PermitCardM job={job} techFirst={techFirst} />}
      {cur === "wrap" && <WrapStep job={job} dispatchId={dispatchId} techFirst={techFirst} empno={empno} onWrap={onWrap} />}
    </>
  );
}

/* ------------------------------------------------------------------ scope */

/** `files` = the crew's documents to show under the scope (installs); null =
 *  they have their own step (new construction). */
function ScopeStep({ job, files, types, money = true, techFirst = null, empno = null, dispatchId = null, onSite = false }: {
  job: Job; files: PegasusFile[] | null; types: JobDocType[]; money?: boolean;
  techFirst?: string | null; empno?: string | null; dispatchId?: string | null;
  /** The crew is at the house running this install (not a preview). */
  onSite?: boolean;
}) {
  const { data: equip } = useJobEquipment(job.id);
  const nc = isNCJob(job);
  const rows = equip ?? [];
  const units = rows.filter((e) => e.kind !== "accessory" && e.kind !== "material");
  const extras = rows.filter((e) => e.kind === "accessory" || e.kind === "material");
  // INSTALL-SPEC-02: only the details that fit this equipment, grouped the
  // way the office asked them (furnace venting, AC, accessories, site).
  const facts = useJobEquipFacts(rows);
  const bank = useInstallBank();
  const specG = specGroups(specLines(job.install_spec, facts, bank), bank);
  return (
    <>
      {!nc && onSite && <BeforeYouStartCard job={job} dispatchId={dispatchId} />}
      <section className={card}>
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="text-[11px] font-extrabold uppercase tracking-[0.12em] text-slate-400">{nc ? "New construction" : "Install"}</div>
            <h4 className="mt-0.5 text-[16px] font-bold leading-snug tracking-tight text-slate-900">{job.title ?? "The job"}</h4>
            {job.address && <p className="mt-0.5 text-[12.5px] text-slate-500">{job.address}</p>}
          </div>
          <Wrench className="h-5 w-5 shrink-0 text-slate-300" />
        </div>
      </section>

      <section className={card}>
        <h4 className="flex items-center gap-2 text-[15px] font-bold tracking-tight text-slate-900"><Package className="h-4 w-4 text-slate-400" /> What&apos;s Going In</h4>
        {rows.length === 0 ? (
          <p className="mt-2 rounded-2xl bg-slate-50 px-3 py-2.5 text-xs text-slate-500">No equipment on the job yet — ask the office.</p>
        ) : (
          <>
            <ul className="mt-2 divide-y divide-slate-100">
              {units.map((e) => (
                <li key={e.id} className="flex items-start gap-3 py-3">
                  <span className={cn("mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-xl", e.serial ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-400")}>
                    {e.serial ? <Check className="h-4 w-4" /> : <Package className="h-4 w-4" />}
                  </span>
                  {/* TECH-READ-01: the model and serial at reading size, both copyable. */}
                  <UnitLines
                    kind={`${e.category || "Equipment"}${e.qty > 1 ? ` × ${e.qty}` : ""}`}
                    brand={e.manufacturer}
                    model={e.model}
                    serial={e.serial}
                    serialFallback={e.pulled_at ? "Picked — serial not logged" : "Not picked yet"}
                    meta={e.serial && e.pulled_at ? "Picked by the warehouse" : null}
                  />
                </li>
              ))}
            </ul>
            {extras.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {extras.map((e) => (
                  <span key={e.id} className="m-chip">{[e.category, e.model].filter(Boolean).join(" · ") || "Accessory"}{e.qty > 1 ? ` × ${e.qty}` : ""}</span>
                ))}
              </div>
            )}
          </>
        )}
      </section>

      {/* MATERIAL-REQ-01: what's been asked for, where it is, and "Need Something?". */}
      <JobMaterialsCard job={job} dispatchId={dispatchId} empno={empno} techFirst={techFirst} />

      {specG.length > 0 && (
        <section className={card}>
          <h4 className="flex items-center gap-2 text-[17px] font-bold tracking-tight text-slate-900"><ClipboardCheck className="h-5 w-5 text-slate-400" /> Installer Guide</h4>
          <div className="mt-2 space-y-4">
            {specG.map((g) => (
              <div key={g.title}>
                <div className="text-[13px] font-bold tracking-wide text-brand-700">{g.title}</div>
                <dl className="mt-1 divide-y divide-slate-100">
                  {g.lines.map((r) => (
                    <div key={r.key} className="py-2">
                      <dt className="text-[14px] font-medium text-slate-500">{r.label}</dt>
                      <dd className="mt-0.5 text-[16px] font-semibold leading-snug text-slate-900">{r.value}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            ))}
          </div>
        </section>
      )}

      {files && (files.length > 0 || onSite) && <DocsStep files={files} types={types} job={onSite ? job : null} />}

      {/* Decision 4 (Seth 2026-09-24): installers see the customer's price and
          balance — never cost or margin. New-construction crews see no money. */}
      {!nc && money && <BalanceCard job={job} collectable={false} />}
      {!nc && onSite && <PermitCardM job={job} techFirst={techFirst} />}
    </>
  );
}

/* -------------------------------------------------------------- documents */

/** `job` (on site) adds Sketch; any photo or PDF can be marked up (MARKUP-03). */
function DocsStep({ files, types, job = null }: { files: PegasusFile[]; types: JobDocType[]; job?: Job | null }) {
  const qc = useQueryClient();
  const urls = useSignedUrls(files).data;
  const [view, setView] = useState<PegasusFile | null>(null);
  const [marking, setMarking] = useState<PegasusFile | null>(null);
  const [sketching, setSketching] = useState(false);
  const groups = types
    .map((t) => ({ t, rows: files.filter((f) => jobDocTypeOf(f, types) === t.key) }))
    .filter((g) => g.rows.length > 0);
  const saved = () => { invalidateFileQueries(qc); haptic("success"); };
  const editors = (
    <>
      {marking && <MarkupEditor file={marking} origin="tech" onClose={() => setMarking(null)} onSaved={() => { setMarking(null); setView(null); saved(); }} />}
      {sketching && job && (
        <MarkupEditor sketch={{ target: { custno: job.custno, locno: job.locno, job_id: job.id, origin: "tech", crew_visible: true }, title: "Sketch" }}
          origin="tech" onClose={() => setSketching(false)} onSaved={() => { setSketching(false); saved(); }} />
      )}
    </>
  );
  const head = (
    <div className="flex items-center gap-2">
      <h4 className="flex items-center gap-2 text-[15px] font-bold tracking-tight text-slate-900"><FolderOpen className="h-4 w-4 text-slate-400" /> Documents</h4>
      {job && (
        <button type="button" onClick={() => setSketching(true)}
          className="ml-auto inline-flex h-10 items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 text-[14px] font-bold text-slate-700 [touch-action:manipulation] active:bg-slate-50">
          <PenLine className="h-4 w-4" /> Sketch
        </button>
      )}
    </div>
  );
  if (files.length === 0) {
    return (
      <section className={card}>
        {head}
        <p className="mt-2 rounded-2xl bg-slate-50 px-3 py-2.5 text-xs leading-relaxed text-slate-500">
          Nothing shared with the crew yet.
        </p>
        {editors}
      </section>
    );
  }
  return (
    <section className={card}>
      {head}
      {groups.map(({ t, rows }) => {
        const photos = rows.filter((f) => f.kind === "photo");
        const docs = rows.filter((f) => f.kind !== "photo");
        return (
          <div key={t.key} className="mt-3">
            <div className="text-[10.5px] font-extrabold uppercase tracking-[0.12em] text-slate-400">{t.label}</div>
            {photos.length > 0 && (
              <div className="mt-1.5 grid grid-cols-4 gap-1.5">
                {photos.map((f) => (
                  <button key={f.id} type="button" onClick={() => setView(f)} className="relative aspect-square overflow-hidden rounded-xl bg-slate-100 [touch-action:manipulation] active:opacity-80">
                    {urls?.get(f.storage_path)
                      ? <img src={urls.get(f.storage_path)} alt={f.filename} className="h-full w-full object-cover" />
                      : <span className="flex h-full w-full items-center justify-center"><Loader2 className="h-4 w-4 animate-spin text-slate-300" /></span>}
                  </button>
                ))}
              </div>
            )}
            {docs.length > 0 && (
              <ul className="mt-1 divide-y divide-slate-100">
                {docs.map((f) => (
                  <li key={f.id} className="flex items-center gap-1">
                    <a href={urls?.get(f.storage_path) ?? undefined} target="_blank" rel="noreferrer" className="flex min-w-0 flex-1 items-center gap-2.5 py-2 active:opacity-70">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-sky-50 text-sky-600"><FileText className="h-4 w-4" /></span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-[13.5px] font-bold text-slate-800">{f.caption?.trim() || f.filename}</span>
                        <span className="block truncate text-[10.5px] text-slate-400">
                          {new Date(f.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" })}{f.size_bytes ? ` · ${prettySize(f.size_bytes)}` : ""}
                        </span>
                      </span>
                      <ChevronRight className="h-4 w-4 shrink-0 text-slate-300" />
                    </a>
                    {(isPdfFile(f) || isImageFile(f)) && (
                      <button type="button" onClick={() => setMarking(f)} aria-label={`Mark up ${f.filename}`}
                        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-slate-500 [touch-action:manipulation] active:bg-slate-100">
                        <PenLine className="h-4 w-4" />
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        );
      })}
      {view && (
        <div className="fixed inset-0 z-50 mx-auto flex max-w-md flex-col bg-black/95">
          <div className="flex items-center justify-between gap-2 p-3">
            <span className="min-w-0 truncate text-xs font-semibold text-white/90">{view.caption?.trim() || view.filename}</span>
            <button type="button" onClick={() => setView(null)} className="rounded-xl p-2 text-white/80 active:bg-white/10" aria-label="Close photo"><X className="h-5 w-5" /></button>
          </div>
          <div className="flex min-h-0 flex-1 items-center justify-center p-2">
            {urls?.get(view.storage_path) ? <img src={urls.get(view.storage_path)} alt="" className="max-h-full max-w-full rounded-xl object-contain" /> : <Loader2 className="h-6 w-6 animate-spin text-white/50" />}
          </div>
          <div className="grid grid-cols-2 gap-2 p-3 pb-[max(env(safe-area-inset-bottom),12px)]">
            {isImageFile(view) && (
              <button type="button" onClick={() => setMarking(view)} className="flex items-center justify-center gap-2 rounded-2xl bg-white py-3.5 text-sm font-bold text-slate-900 active:bg-white/90">
                <PenLine className="h-4 w-4" /> Mark Up
              </button>
            )}
            <a href={urls?.get(view.storage_path)} target="_blank" rel="noreferrer" className={cn("flex items-center justify-center gap-2 rounded-2xl bg-white/10 py-3.5 text-sm font-bold text-white active:bg-white/20", !isImageFile(view) && "col-span-2")}>Open full size</a>
          </div>
        </div>
      )}
      {editors}
    </section>
  );
}

/* ----------------------------------------------------------------- permit */

const PERMIT_CHIP: Record<string, string> = {
  requested: "m-chip-warn", applied: "m-chip-tint", issued: "m-chip-tint", finaled: "m-chip-good", cancelled: "",
};
const fmtDay = (iso: string | null) =>
  iso ? new Date(`${iso}T12:00:00`).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }) : "date not set";
const whenShort = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString("en-US", { weekday: "short", hour: "numeric", minute: "2-digit" }) : "";
const permitName = (trade: string | null) => {
  const t = (trade ?? "").trim() || "Mechanical";
  return /permit/i.test(t) ? t : `${t} permit`;
};

/** PERMIT-01 on the phone — status, inspections, and "Request a permit". */
export function PermitCardM({ job, techFirst }: { job: Job; techFirst: string | null }) {
  const { data: permits } = useJobPermits(job.id);
  const { data: inspections } = usePermitInspections(job.id);
  const { data: postInstall } = usePostInstall({ dispatch: job.source_dispatch ?? null, jobId: job.id });
  const by = effectivePermitBy(job, postInstall ?? null);
  const list = (permits ?? []).filter((p) => p.status !== "cancelled");
  const pending = list.filter((p) => p.status === "requested" && !p.by_builder);
  const ours = list.filter((p) => !(p.status === "requested" && !p.by_builder));
  const [asking, setAsking] = useState(false);

  const header = by === "builder" ? "The builder pulls the permit"
    : by === "none" ? "No permit needed on this job"
    : ours.some(permitIsOnFile) ? "Permit on file"
    : pending.length ? "Permit requested — the office has it"
    : by === null ? "Who pulls the permit isn't set yet"
    : "No permit yet";

  return (
    <section className={card}>
      <h4 className="flex items-center gap-2 text-[15px] font-bold tracking-tight text-slate-900"><ShieldCheck className="h-4 w-4 text-slate-400" /> Permit</h4>
      <p className={cn("mt-1 text-[13px] font-semibold", by === "none" || ours.some(permitIsOnFile) ? "text-emerald-700" : pending.length ? "text-amber-700" : "text-slate-600")}>{header}</p>

      {ours.length > 0 && (
        <ul className="mt-2 space-y-2">
          {ours.map((p) => (
            <PermitRowM key={p.id} p={p} inspections={(inspections ?? []).filter((i) => i.permit_id === p.id)} />
          ))}
        </ul>
      )}
      {pending.map((p) => (
        <div key={p.id} className="mt-2 rounded-2xl bg-amber-50 px-3 py-2.5 text-[12.5px] text-amber-900">
          <div className="flex items-center gap-1.5 font-bold"><HardHat className="h-3.5 w-3.5" /> {permitName(p.trade)} requested{p.requested_by ? ` by ${p.requested_by.includes("@") ? p.requested_by.split("@")[0] : p.requested_by}` : ""}</div>
          <div className="mt-0.5 text-amber-800">{whenShort(p.requested_at)}{p.request_note ? ` · “${p.request_note}”` : ""}</div>
        </div>
      ))}

      {by !== "none" && by !== "builder" && !ours.some(permitIsOnFile) && (
        asking ? (
          <RequestPermitSheet job={job} techFirst={techFirst} onDone={() => setAsking(false)} />
        ) : (
          <button type="button" onClick={() => setAsking(true)} className={cn("m-btn mt-3 w-full", pending.length ? "m-btn-soft" : "m-btn-tint")}>
            <HardHat className="h-[18px] w-[18px]" /> {pending.length ? "Request Another Permit" : "Request a Permit"}
          </button>
        )
      )}
      {by === "builder" && ours.length === 0 && (
        <p className="mt-2 text-[12px] leading-snug text-slate-500">It should be posted on site — if it isn&apos;t, tell the office.</p>
      )}
    </section>
  );
}

function PermitRowM({ p, inspections }: { p: JobPermit; inspections: PermitInspection[] }) {
  return (
    <li className="rounded-2xl bg-slate-50 px-3 py-2.5">
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-[13.5px] font-bold text-slate-800">{permitName(p.trade)}</span>
        <span className={cn("m-chip", PERMIT_CHIP[p.status])}>{PERMIT_STATUS_LABEL[p.status]}</span>
        {p.by_builder && <span className="m-chip">Builder&apos;s</span>}
      </div>
      <div className="mt-0.5 text-[12px] text-slate-500">
        {[p.permit_no ? `#${p.permit_no}` : "number not in yet", p.jurisdiction].filter(Boolean).join(" · ")}
      </div>
      {inspections.length > 0 && (
        <ul className="mt-1.5 space-y-0.5">
          {inspections.map((i) => (
            <li key={i.id} className="flex items-center gap-1.5 text-[12px]">
              <ClipboardCheck className="h-3.5 w-3.5 text-slate-400" />
              <span className="font-semibold text-slate-700">{INSPECTION_KIND_LABEL[i.kind]}</span>
              <span className="text-slate-500">· {fmtDay(i.scheduled_date)}</span>
              <span className={cn("font-semibold", i.result === "passed" ? "text-emerald-700" : i.result === "failed" ? "text-rose-600" : "text-slate-500")}>· {INSPECTION_RESULT_LABEL[i.result]}</span>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

function RequestPermitSheet({ job, techFirst, onDone }: { job: Job; techFirst: string | null; onDone: () => void }) {
  const req = useRequestPermit();
  const [trade, setTrade] = useState("Mechanical");
  const [note, setNote] = useState("");
  return (
    <div className="mt-3 rounded-2xl border border-slate-200 bg-slate-50 p-3">
      <div className="text-[12.5px] font-bold text-slate-700">Which permit?</div>
      <div className="m-seg mt-1.5">
        {["Mechanical", "Electrical", "Plumbing", "Gas"].map((t) => (
          <button key={t} type="button" onClick={() => setTrade(t)} className={cn(trade === t && "on")}>{t}</button>
        ))}
      </div>
      <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2}
        placeholder="Why now? e.g. inspector coming Thursday, none posted on site"
        className="mt-2 w-full resize-none rounded-xl border border-slate-200 bg-white px-3 py-2 text-[14px] text-slate-800 placeholder:text-slate-400 focus:border-slate-400 focus:outline-none" />
      <div className="mt-2 grid grid-cols-2 gap-2">
        <button type="button" onClick={onDone} className="m-btn m-btn-soft">Cancel</button>
        <button type="button" disabled={req.isPending}
          onClick={() => req.mutate({ job_id: job.id, trade, note, requestedBy: techFirst, jurisdiction: job.service_city ?? null }, { onSuccess: () => { haptic("success"); onDone(); } })}
          className="m-btn m-btn-primary">
          {req.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send Request
        </button>
      </div>
      {req.isError && <p className="mt-1.5 text-center text-[12px] font-semibold text-rose-600">Didn&apos;t send — check the signal and try again.</p>}
    </div>
  );
}

/* ---------------------------------------------------------------- wrap up */

const FINISH_REASONS = ["Customer wasn't home", "Coming back to finish", "The office will handle it", "Other"];

function WrapStep({ job, dispatchId, techFirst, empno = null, onWrap }: { job: Job; dispatchId: string | null; techFirst: string | null; empno?: string | null; onWrap: () => void }) {
  const qc = useQueryClient();
  const updJob = useUpdateJob();
  const install = !isNCJob(job);
  const ready = useInstallReadiness(job);
  const crewFinish = useCrewFinish(job.id);
  const [asking, setAsking] = useState(false);
  const [why, setWhy] = useState("");
  const [whyNote, setWhyNote] = useState("");
  const [custNote, setCustNote] = useState(job.invoice_message ?? "");
  const [officeNote, setOfficeNote] = useState("");
  const [sent, setSent] = useState<string[]>([]);
  const custDirty = custNote.trim() !== (job.invoice_message ?? "").trim();

  const postOffice = useMutation({
    mutationFn: async (body: string) => {
      const { data: u } = await supabase.auth.getUser();
      const { error } = await supabase.from("pegasus_internal_note").insert({
        entity_type: "job", entity_id: job.id, body,
        author_name: techFirst, author_email: u?.user?.email ?? null,
      });
      if (error) throw error;
    },
    onSuccess: (_d, body) => { setSent((s) => [body, ...s]); setOfficeNote(""); void qc.invalidateQueries({ queryKey: ["internal_notes", "job", job.id] }); },
  });

  // Install complete = the VISIT is done (the office closes the job — the
  // money finish line stays in the office). Done for today = the visit rides
  // forward to tomorrow on its own.
  const finish = useMutation({
    mutationFn: async (complete: boolean) => {
      if (custDirty) await updJob.mutateAsync({ id: job.id, invoice_message: custNote.trim() || null });
      // INSTALLER-APP-01b: Install Complete stamps the job crew-finished → the
      // office's review; anything missing rides along with the crew's why.
      if (complete && install) {
        const missingNote = ready.missing.length
          ? `${ready.missing.join("; ")} — ${why === "Other" ? (whyNote.trim() || "Other") : why}${why !== "Other" && whyNote.trim() ? `: ${whyNote.trim()}` : ""}`
          : null;
        await crewFinish.mutateAsync({ note: missingNote });
      }
      if (complete && dispatchId) {
        const { error } = await supabase.from("pegasus_dispatch")
          .update({ status: "completed", updated_at: new Date().toISOString() }).eq("id", dispatchId);
        if (error) throw error;
      }
      const { data: u } = await supabase.auth.getUser();
      await supabase.from("pegasus_internal_note").insert({
        entity_type: "job", entity_id: job.id,
        body: complete
          ? `${isNCJob(job) ? "Visit" : "Install"} complete — ${techFirst ?? "the crew"} marked it done on the iPad.`
            + (install && ready.missing.length
              // The crew's own words may already end in a period — never "..".
              ? ((w) => ` Still missing: ${ready.missing.join("; ")}. Why: ${w}${/[.!?]$/.test(w) ? "" : "."}`)(
                  `${why === "Other" ? (whyNote.trim() || "Other") : why}${why !== "Other" && whyNote.trim() ? ` — ${whyNote.trim()}` : ""}`.trim())
              : "")
          : `${techFirst ?? "The crew"} is done for today — coming back.`,
        author_name: techFirst, author_email: u?.user?.email ?? null,
      });
    },
    onSuccess: () => {
      haptic("success");
      void qc.invalidateQueries({ queryKey: ["scheduled_board"] });
      void qc.invalidateQueries({ queryKey: ["internal_notes", "job", job.id] });
      onWrap();
    },
  });

  const onComplete = () => {
    if (install && ready.missing.length) { setAsking(true); return; }
    finish.mutate(true);
  };

  return (
    <>
      {install && (
        <>
          <ReadyToFinishCard r={ready} />
          <AfterPhotosCard job={job} r={ready} />
          <InstallChecklistCard job={job} empno={empno} />
          <RebateTestCard job={job} empno={empno} techFirst={techFirst} />
          <SignOffCard job={job} />
          <BalanceCard job={job} collectable />
        </>
      )}
      <section className={card}>
        <h4 className="text-[15px] font-bold tracking-tight text-slate-900">Note on Their Invoice</h4>
        <div className="mt-2">
          <NotesEditor value={custNote} onChange={setCustNote} rows={3} placeholder="e.g. New filter is 16×25×4 — change it every 6 months." />
        </div>
        {custDirty && (
          <button type="button" disabled={updJob.isPending} onClick={() => updJob.mutate({ id: job.id, invoice_message: custNote.trim() || null })} className="m-btn m-btn-tint mt-2 w-full">
            {updJob.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Save Customer Note
          </button>
        )}
      </section>

      <section className={card}>
        <h4 className="flex items-center gap-2 text-[15px] font-bold tracking-tight text-slate-900"><Lock className="h-4 w-4 text-amber-500" /> Note for the Office</h4>
        <div className="mt-2">
          <NotesEditor value={officeNote} onChange={setOfficeNote} rows={2} placeholder="e.g. Need a 3-ft section of B-vent — coming back Thursday." />
        </div>
        <button type="button" disabled={!officeNote.trim() || postOffice.isPending} onClick={() => postOffice.mutate(officeNote.trim())} className="m-btn m-btn-soft mt-2 w-full">
          {postOffice.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send to the Office
        </button>
        {sent.length > 0 && (
          <ul className="mt-2 space-y-1">
            {sent.map((b, i) => <li key={i} className="flex items-start gap-1.5 text-[12px] text-slate-500"><Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" /> {b}</li>)}
          </ul>
        )}
      </section>

      <div className="grid gap-2.5">
        <button type="button" disabled={finish.isPending} onClick={onComplete} className="m-btn m-btn-good m-btn-big w-full">
          {finish.isPending ? <Loader2 className="h-[18px] w-[18px] animate-spin" /> : <Check className="h-[18px] w-[18px]" />}
          {isNCJob(job) ? "Visit Complete" : "Install Complete"} — Tell the Office
        </button>
        <button type="button" disabled={finish.isPending} onClick={() => finish.mutate(false)} className="m-btn m-btn-soft w-full">
          Done for Today — Coming Back
        </button>
        {finish.isError && <p className="text-center text-[12px] font-semibold text-rose-600">Didn&apos;t save — check the signal and try again.</p>}
      </div>

      {asking && (
        <Sheet title="Something's Missing" sub={job.customer_name ?? job.title} onClose={() => setAsking(false)}
          footer={
            <button type="button" disabled={!why || (why === "Other" && !whyNote.trim()) || finish.isPending}
              onClick={() => finish.mutate(true, { onSuccess: () => setAsking(false) })} className="m-btn m-btn-good m-btn-big w-full">
              {finish.isPending ? <Loader2 className="h-[18px] w-[18px] animate-spin" /> : <Check className="h-[18px] w-[18px]" />} Send to the Office Anyway
            </button>
          }>
          <section className={card}>
            <ul className="space-y-1.5">
              {ready.missing.map((m) => (
                <li key={m} className="flex items-start gap-2 text-[14px] font-semibold text-slate-800"><span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500" /> {m}</li>
              ))}
            </ul>
          </section>
          <section className={card}>
            <div className="text-[13px] font-bold text-slate-700">Why?</div>
            <div className="mt-2"><ReasonChips options={FINISH_REASONS} value={why} onPick={setWhy} /></div>
            <textarea value={whyNote} onChange={(e) => setWhyNote(e.target.value)} rows={2} placeholder={why === "Other" ? "Tell the office what happened" : "Anything else? (optional)"}
              className="mt-2.5 w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-[15px] text-slate-800 placeholder:text-slate-400 focus:border-slate-400 focus:outline-none" />
          </section>
        </Sheet>
      )}
    </>
  );
}

/* ------------------------------------------ installer Today (FIELD-CREWS-01) */

interface InstallDayVisit {
  id: string; job_id: string; visit_role: string | null; summary: string | null; customer_name: string | null;
  address: string | null; city: string | null; scheduled_date: string; scheduled_window: string | null;
  assigned_empno: string | null; status: string | null; lane_key: string | null;
}
const isoLocal = (d: Date) => d.toLocaleDateString("en-CA");

/** Decision 7 (Seth 2026-09-24): installers see today plus tomorrow's scope.
 *  Their own install runs as the Today hero like any call; these cards add
 *  the next workday's installs (what's going in + the documents — load the
 *  truck the night before) and every install on the board today. */
export function InstallerDayCards({ empno, nameOf }: { empno: string | null; nameOf: (e: string | null) => string | null }) {
  const today = isoLocal(new Date());
  const next = nextPickupDay(today);
  useRealtimeRefresh(["pegasus_dispatch"], [["m_install_days"]]);
  const { data, isLoading } = useQuery({
    queryKey: ["m_install_days", today, next],
    refetchInterval: 60 * 1000,
    queryFn: async (): Promise<InstallDayVisit[]> => {
      const { data: rows, error } = await supabase.from("pegasus_dispatch")
        .select("id, job_id, visit_role, summary, customer_name, address, city, scheduled_date, scheduled_window, assigned_empno, status, lane_key")
        .not("job_id", "is", null)
        .in("scheduled_date", [today, next])
        .neq("status", "cancelled")
        .is("deleted_at", null)
        .order("scheduled_date")
        .order("scheduled_window", { nullsFirst: false })
        .order("id");
      if (error) throw error;
      return ((rows ?? []) as InstallDayVisit[]).filter((v) => isJobWorkVisit(v));
    },
  });
  const [peek, setPeek] = useState<InstallDayVisit | null>(null);
  if (isLoading) return <div className="h-24 animate-pulse rounded-3xl bg-slate-200/60" />;
  const rows = data ?? [];
  const mineNext = rows.filter((v) => v.scheduled_date === next && !!empno && v.assigned_empno === empno);
  const crewOf = (v: InstallDayVisit) => (v.assigned_empno && v.assigned_empno === empno ? "You" : (nameOf(v.assigned_empno) ?? "").split(/\s+/)[0] || "Not assigned");
  const allToday = rows
    .filter((v) => v.scheduled_date === today)
    .sort((a, b) => Number(b.assigned_empno === empno) - Number(a.assigned_empno === empno) || crewOf(a).localeCompare(crewOf(b)));
  const nextLabel = new Date(`${next}T12:00:00`).toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" });
  return (
    <>
      <section className={card}>
        <div className="flex items-baseline justify-between gap-2">
          <h4 className="text-[15px] font-bold tracking-tight text-slate-900">Your Next Install{mineNext.length > 1 ? "s" : ""}</h4>
          <span className="shrink-0 text-[12px] font-semibold text-slate-400">{nextLabel}</span>
        </div>
        {mineNext.length === 0 ? (
          <p className="mt-1.5 text-[12.5px] text-slate-500">Nothing on your install schedule yet.</p>
        ) : (
          <>
            <ul className="mt-1 divide-y divide-slate-100">
              {mineNext.map((v) => <InstallVisitRow key={v.id} v={v} crew={null} onOpen={() => setPeek(v)} />)}
            </ul>
          </>
        )}
      </section>
      {/* Only worth a card when someone else is installing today too — their
          own install is already the hero above. */}
      {allToday.some((v) => v.assigned_empno !== empno) && (
        <section className={card}>
          <h4 className="text-[15px] font-bold tracking-tight text-slate-900">Every Install Today</h4>
          <ul className="mt-1 divide-y divide-slate-100">
            {allToday.map((v) => <InstallVisitRow key={v.id} v={v} crew={crewOf(v)} onOpen={() => setPeek(v)} />)}
          </ul>
        </section>
      )}
      {peek && <TechPortal><InstallPeek v={peek} empno={empno} techFirst={(nameOf(empno) ?? "").split(/\s+/)[0] || null} onClose={() => setPeek(null)} /></TechPortal>}
    </>
  );
}

/** Full-screen panes opened from the Today list must render OUTSIDE <main>:
 *  main's entry animation leaves a transform on it, and a transformed parent
 *  traps position:fixed children (the pane rendered 1px wide, off to the
 *  side). The portal keeps them inside the tech app's root so its styles
 *  still apply. */
function TechPortal({ children }: { children: ReactNode }) {
  if (typeof document === "undefined") return null;
  const root = (document.querySelector("[data-tech-app]") as HTMLElement | null) ?? document.body;
  return createPortal(children, root);
}

function InstallVisitRow({ v, crew, onOpen }: { v: InstallDayVisit; crew: string | null; onOpen: () => void }) {
  return (
    <li>
      <button type="button" onClick={() => { haptic("light"); onOpen(); }} className="flex w-full items-center gap-2.5 py-2.5 text-left active:opacity-70">
        {crew && <span className={cn("m-chip shrink-0", crew === "You" && "m-chip-tint")}>{crew}</span>}
        <span className="min-w-0 flex-1">
          <span className="block truncate text-[14px] font-bold text-slate-800">{v.customer_name ?? "Customer"}</span>
          <span className="block truncate text-[11.5px] text-slate-500">
            {[v.address, v.city].filter(Boolean).join(", ") || v.summary || "address on the job"}{v.scheduled_window ? ` · ${v.scheduled_window}` : ""}
          </span>
        </span>
        {v.status === "completed" && <span className="m-chip m-chip-good shrink-0">Done</span>}
        <ChevronRight className="h-4 w-4 shrink-0 text-slate-300" />
      </button>
    </li>
  );
}

/** A read-only look at an install that isn't the one being run — what's
 *  going in, the install details and the crew's documents. */
function InstallPeek({ v, empno, techFirst, onClose }: { v: InstallDayVisit; empno: string | null; techFirst: string | null; onClose: () => void }) {
  const { data: job, isLoading } = useJob(v.job_id);
  const { data: files } = useJobFiles(v.job_id);
  const { data: typesQ } = useJobDocTypes();
  const types = typesQ ?? JOB_DOC_TYPES_DEFAULT;
  const crewFiles = useMemo(() => (files ?? []).filter((f) => crewCanSee(f, types)), [files, types]);
  const day = new Date(`${v.scheduled_date}T12:00:00`).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
  return (
    <div className="fixed inset-0 z-[45] mx-auto flex max-w-md flex-col bg-[#f6f5f1]">
      <header className="flex items-center gap-2 border-b border-slate-200 bg-white px-3 pb-2.5 pt-[max(env(safe-area-inset-top),10px)]">
        <button type="button" onClick={onClose} className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-100 text-slate-600 [touch-action:manipulation] active:scale-95" aria-label="Back">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="min-w-0 flex-1">
          <h2 className="truncate text-[17px] font-extrabold tracking-tight text-slate-900">{v.customer_name ?? "Install"}</h2>
          <p className="truncate text-[11.5px] text-slate-500">{day}{v.scheduled_window ? ` · ${v.scheduled_window}` : ""}{v.city ? ` · ${v.city}` : ""}</p>
        </div>
      </header>
      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-3 pb-10">
        {isLoading ? (
          <div className="h-40 animate-pulse rounded-3xl bg-slate-200/60" />
        ) : !job ? (
          <div className={card}><p className="text-sm font-semibold text-slate-700">This install&apos;s job isn&apos;t available.</p></div>
        ) : (
          <>
            <ScopeStep job={job} files={null} types={types} money={false} empno={empno} techFirst={techFirst} dispatchId={v.id} />
            <DocsStep files={crewFiles} types={types} />
          </>
        )}
      </div>
    </div>
  );
}
